import { InventoryItem, ReplenishmentRequest } from '../types';

export type AutoReplenishmentCategory = 'consumable' | 'stationery' | 'equipment' | 'manual';
export type AutoReplenishmentReason = 'expired' | 'low_stock' | 'zero_stock';

export interface AutoReplenishmentProposal {
  id: string;
  eventId?: string;
  eventLabel?: string;
  department: string;
  name: string;
  unit: string;
  category: AutoReplenishmentCategory;
  categoryLabel: string;
  reason: AutoReplenishmentReason;
  reasonLabel: string;
  currentQuantity: number;
  availableQuantity: number;
  expiredQuantity: number;
  requestedQuantity?: number;
  minimumQuantity?: number;
  targetQuantity?: number;
  itemIds: string[];
  expiryBreakdown: string;
  note: string;
  ruleSummary: string;
}

interface InventoryGroup {
  id: string;
  eventId?: string;
  eventLabel?: string;
  department: string;
  name: string;
  unit: string;
  items: InventoryItem[];
}

const CATEGORY_LABELS: Record<AutoReplenishmentCategory, string> = {
  consumable: '常用消耗品',
  stationery: '行政/文具',
  equipment: '器材類',
  manual: '需人工判斷',
};

const LOW_STOCK_UNITS = new Map<string, { minimumQuantity: number; targetQuantity: number }>([
  ['包', { minimumQuantity: 1, targetQuantity: 2 }],
  ['盒', { minimumQuantity: 1, targetQuantity: 2 }],
  ['卷', { minimumQuantity: 1, targetQuantity: 2 }],
  ['捲', { minimumQuantity: 1, targetQuantity: 2 }],
  ['瓶', { minimumQuantity: 1, targetQuantity: 2 }],
  ['罐', { minimumQuantity: 1, targetQuantity: 2 }],
  ['袋', { minimumQuantity: 1, targetQuantity: 2 }],
  ['組', { minimumQuantity: 1, targetQuantity: 2 }],
  ['支', { minimumQuantity: 5, targetQuantity: 10 }],
  ['片', { minimumQuantity: 5, targetQuantity: 10 }],
  ['個', { minimumQuantity: 5, targetQuantity: 10 }],
]);

const ZERO_ONLY_UNITS = new Set(['批', '份']);

export const getAutoReplenishmentCategory = (item: Pick<InventoryItem, 'name' | 'unit' | 'note'>): AutoReplenishmentCategory => {
  const text = `${item.name || ''} ${item.unit || ''} ${item.note || ''}`;

  if (/夾鏈袋|藥袋|機器包藥紙|電池|衛生紙|便條紙|文具|筆|長尾夾|迴紋針|小人牌|科牌|衛教單|板夾|貼紙|表單/.test(text)) {
    return 'stationery';
  }

  if (/血壓計|額溫槍|紅外線測溫器|小型額溫槍|體重機|體重器|電子秤|研缽|研缽棒|計時器|聽診器|血氧機|膠台|彎盆|手枕/.test(text)) {
    return 'equipment';
  }

  if (/德銳適|針|酒精|酒棉|棉球|棉棒|口腔棉棒|紗布|繃帶|膠帶|手套|敷布|敷料|優碘|壓舌板|生理食鹽水/.test(text)) {
    return 'consumable';
  }

  return 'manual';
};

const normalizeEventToken = (value?: string) => (value || '').trim();

export const matchesInventoryEvent = (
  record: { eventId?: string; eventLabel?: string },
  selectedEventId: string,
  selectedEventLabel: string
) => {
  if (!selectedEventId) return !record.eventId && !record.eventLabel;
  return record.eventId === selectedEventId ||
    (!record.eventId && normalizeEventToken(record.eventLabel) === normalizeEventToken(selectedEventLabel));
};

const isExpired = (expiryDate: string | undefined, referenceDate: Date) => {
  if (!expiryDate) return false;
  const expiry = new Date(`${expiryDate}T00:00:00`);
  if (Number.isNaN(expiry.getTime())) return false;

  const today = new Date(referenceDate);
  today.setHours(0, 0, 0, 0);
  return expiry.getTime() < today.getTime();
};

const groupInventoryItems = (
  items: InventoryItem[],
  selectedEventId: string,
  selectedEventLabel: string,
  selectedDepartment: string
): InventoryGroup[] => {
  const groups = new Map<string, InventoryGroup>();

  items
    .filter(item => item.isDeleted !== true)
    .filter(item => matchesInventoryEvent(item, selectedEventId, selectedEventLabel))
    .filter(item => selectedDepartment === '全部' || item.department === selectedDepartment)
    .forEach(item => {
      const groupId = [
        normalizeEventToken(item.eventId),
        normalizeEventToken(item.eventLabel),
        item.department,
        item.name.trim(),
        item.unit.trim(),
      ].join('|');

      const group = groups.get(groupId) || {
        id: groupId,
        eventId: item.eventId,
        eventLabel: item.eventLabel,
        department: item.department,
        name: item.name.trim(),
        unit: item.unit.trim(),
        items: [],
      };
      group.items.push(item);
      groups.set(groupId, group);
    });

  return Array.from(groups.values()).sort((a, b) =>
    a.department.localeCompare(b.department, 'zh-Hant') ||
    a.name.localeCompare(b.name, 'zh-Hant') ||
    a.unit.localeCompare(b.unit, 'zh-Hant')
  );
};

const hasOpenRequestForGroup = (
  group: InventoryGroup,
  requests: ReplenishmentRequest[],
  selectedEventId: string,
  selectedEventLabel: string
) => {
  const itemIds = new Set(group.items.map(item => item.id));

  return requests.some(request => {
    if (request.status !== 'submitted') return false;
    if (request.department !== group.department) return false;
    if (!matchesInventoryEvent(request, selectedEventId, selectedEventLabel)) return false;

    return request.items.some(line =>
      line.name === group.name &&
      line.unit === group.unit &&
      (
        !line.inventoryItemId ||
        itemIds.has(line.inventoryItemId) ||
        (line.inventoryItemIds || []).some(itemId => itemIds.has(itemId))
      )
    );
  });
};

const makeExpiryBreakdown = (group: InventoryGroup, referenceDate: Date) =>
  group.items
    .map(item => {
      const expiry = item.expiryDate || '未填效期';
      const status = isExpired(item.expiryDate, referenceDate) ? '已過期' : '可用';
      return `${expiry}：${item.quantity} ${item.unit}（${status}）`;
    })
    .join('；');

const makeProposal = (
  group: InventoryGroup,
  category: AutoReplenishmentCategory,
  reason: AutoReplenishmentReason,
  quantities: {
    currentQuantity: number;
    availableQuantity: number;
    expiredQuantity: number;
    requestedQuantity?: number;
    minimumQuantity?: number;
    targetQuantity?: number;
  },
  referenceDate: Date
): AutoReplenishmentProposal => {
  const reasonLabel = reason === 'expired'
    ? '效期已過期'
    : reason === 'low_stock'
      ? '低於最低庫存'
      : '數量為 0';
  const categoryLabel = CATEGORY_LABELS[category];
  const ruleSummary = reason === 'expired'
    ? '過期品項建議補回過期數量'
    : quantities.minimumQuantity !== undefined && quantities.targetQuantity !== undefined
      ? `最低庫存 ${quantities.minimumQuantity}，補到 ${quantities.targetQuantity}`
      : '數量為 0 時開單，申請數量由人工決定';
  const quantityNote = quantities.requestedQuantity !== undefined
    ? `建議補 ${quantities.requestedQuantity} ${group.unit}`
    : '申請數量待人工決定';

  return {
    id: group.id,
    eventId: group.eventId,
    eventLabel: group.eventLabel,
    department: group.department,
    name: group.name,
    unit: group.unit,
    category,
    categoryLabel,
    reason,
    reasonLabel,
    currentQuantity: quantities.currentQuantity,
    availableQuantity: quantities.availableQuantity,
    expiredQuantity: quantities.expiredQuantity,
    requestedQuantity: quantities.requestedQuantity,
    minimumQuantity: quantities.minimumQuantity,
    targetQuantity: quantities.targetQuantity,
    itemIds: group.items.map(item => item.id),
    expiryBreakdown: makeExpiryBreakdown(group, referenceDate),
    note: `${reasonLabel}；可用庫存 ${quantities.availableQuantity} ${group.unit}；${quantityNote}`,
    ruleSummary,
  };
};

const totalQuantity = (items: InventoryItem[]) =>
  items.reduce((sum, item) => sum + (Number.isFinite(item.quantity) ? item.quantity : 0), 0);

export const buildAutoReplenishmentProposals = (
  items: InventoryItem[],
  requests: ReplenishmentRequest[],
  selectedEventId: string,
  selectedEventLabel: string,
  selectedDepartment: string,
  referenceDate = new Date()
): AutoReplenishmentProposal[] => {
  const groups = groupInventoryItems(items, selectedEventId, selectedEventLabel, selectedDepartment);

  return groups.flatMap(group => {
    if (hasOpenRequestForGroup(group, requests, selectedEventId, selectedEventLabel)) {
      return [];
    }

    const category = getAutoReplenishmentCategory(group.items[0]);
    if (category === 'equipment' || category === 'manual') return [];

    const expiredItems = group.items.filter(item => isExpired(item.expiryDate, referenceDate));
    const availableItems = group.items.filter(item => !isExpired(item.expiryDate, referenceDate));
    const currentQuantity = totalQuantity(group.items);
    const expiredQuantity = totalQuantity(expiredItems);
    const availableQuantity = totalQuantity(availableItems);

    if (expiredQuantity > 0) {
      return [makeProposal(group, category, 'expired', {
        currentQuantity,
        availableQuantity,
        expiredQuantity,
        requestedQuantity: expiredQuantity,
      }, referenceDate)];
    }

    if (category === 'stationery' || ZERO_ONLY_UNITS.has(group.unit)) {
      if (availableQuantity <= 0) {
        return [makeProposal(group, category, 'zero_stock', {
          currentQuantity,
          availableQuantity,
          expiredQuantity,
        }, referenceDate)];
      }
      return [];
    }

    const lowStockRule = LOW_STOCK_UNITS.get(group.unit);
    if (!lowStockRule) return [];

    if (availableQuantity <= lowStockRule.minimumQuantity) {
      return [makeProposal(group, category, 'low_stock', {
        currentQuantity,
        availableQuantity,
        expiredQuantity,
        requestedQuantity: Math.max(lowStockRule.targetQuantity - availableQuantity, 0),
        minimumQuantity: lowStockRule.minimumQuantity,
        targetQuantity: lowStockRule.targetQuantity,
      }, referenceDate)];
    }

    return [];
  });
};
